import React, { useState, useRef, useEffect } from 'react';
import { followUser, uploadFile, getMediaUrl } from '../services/api';

// Shared connection-request modal — same markup the Network and Profile
// pages already render inline, so its visual identity stays consistent
// across surfaces (post header, profile card, network suggestions). The
// canonical `.connect-modal-*` styles live in App.css.
//
// Props:
//   person        — { id, full_name, username, profile_image, job_title }
//   isOpen        — boolean
//   onClose       — called on cancel / outside-click / X
//   onSent        — called after a successful follow/connect, optionally
//                   with { status: 'pending' }
function ConnectModal({ person, isOpen, onClose, onSent }) {
  const [message, setMessage] = useState('');
  const [media, setMedia] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [mediaFile, setMediaFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const modalRef = useRef(null);

  // Reset whenever the modal closes so the next open starts clean.
  useEffect(() => {
    if (!isOpen) {
      setMessage('');
      setMedia(null);
      setMediaType(null);
      setMediaFile(null);
      setError('');
      setSending(false);
    }
  }, [isOpen]);

  // Outside-click closes — match Network/Profile behavior.
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen || !person) return null;

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMediaFile(file);
    setMediaType('image');
    const reader = new FileReader();
    reader.onload = (ev) => setMedia(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMediaFile(file);
    setMediaType('video');
    const reader = new FileReader();
    reader.onload = (ev) => setMedia(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeMedia = () => {
    setMedia(null);
    setMediaType(null);
    setMediaFile(null);
  };

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      let mediaUrl = null;
      if (mediaFile) {
        const uploadRes = await uploadFile(mediaFile);
        mediaUrl = uploadRes.data?.url;
      }

      // Match Network/Profile: media is encoded as a [image:url] / [video:url]
      // tag appended to the message body so the recipient sees it inline.
      let finalMessage = message.trim() || undefined;
      if (mediaUrl) {
        const tag = mediaType === 'image' ? `[image:${mediaUrl}]` : `[video:${mediaUrl}]`;
        finalMessage = finalMessage ? `${finalMessage}\n${tag}` : tag;
      }

      await followUser(person.id, finalMessage);
      onSent?.({ status: 'pending' });
      onClose();
    } catch (err) {
      const apiError = err?.response?.data?.error;
      // Backend rejects a duplicate follow with this exact message — treat it
      // as success since the relationship already matches "pending."
      if (apiError === 'Already following this user') {
        onSent?.({ status: 'pending' });
        onClose();
      } else {
        setError(apiError || 'Could not send the connection request. Try again.');
      }
    }
    setSending(false);
  };

  const initial =
    person.full_name?.charAt(0)?.toUpperCase() ||
    person.username?.charAt(0)?.toUpperCase() ||
    '?';

  return (
    <div className="connect-modal-overlay">
      <div className="connect-modal" ref={modalRef}>
        <div className="connect-modal-header">
          <div className="connect-modal-person">
            <div className="connect-modal-avatar">
              {person.profile_image ? (
                <img
                  src={getMediaUrl(person.profile_image)}
                  alt={person.full_name || person.username || ''}
                />
              ) : (
                initial
              )}
            </div>
            <div className="connect-modal-person-info">
              <div className="connect-modal-name">{person.full_name || person.username}</div>
              {person.job_title && <div className="connect-modal-title">{person.job_title}</div>}
            </div>
          </div>
          <button
            type="button"
            className="connect-modal-close"
            onClick={onClose}
            aria-label="Close connection modal"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="connect-modal-message">
          <label className="connect-modal-label">Add a message (optional)</label>
          <textarea
            className="connect-modal-textarea"
            placeholder="Hi! I'd love to connect with you..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>

        <div className="connect-modal-media">
          <label className="connect-modal-label">Attach media (optional)</label>
          <div className="connect-modal-media-btns">
            <button
              type="button"
              className="connect-modal-media-btn"
              onClick={() => photoInputRef.current?.click()}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              Photo
            </button>
            <button
              type="button"
              className="connect-modal-media-btn"
              onClick={() => videoInputRef.current?.click()}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polygon points="23 7 16 12 23 17 23 7"></polygon>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
              </svg>
              Video
            </button>
          </div>
          <input
            type="file"
            ref={photoInputRef}
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handlePhotoUpload}
            aria-label="Connection photo"
          />
          <input
            type="file"
            ref={videoInputRef}
            accept="video/*"
            style={{ display: 'none' }}
            onChange={handleVideoUpload}
            aria-label="Connection video"
          />

          {media && (
            <div className="connect-modal-preview">
              {mediaType === 'image' ? (
                <img src={media} alt="Preview" className="connect-modal-preview-img" />
              ) : (
                <video src={media} className="connect-modal-preview-video" controls />
              )}
              <button
                type="button"
                className="connect-modal-preview-remove"
                onClick={removeMedia}
                aria-label="Remove attached media"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          )}
        </div>

        {error && <div className="connect-modal-error">{error}</div>}

        <div className="connect-modal-actions">
          <button
            type="button"
            className="connect-modal-cancel"
            onClick={onClose}
            disabled={sending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="connect-modal-send"
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? 'Sending...' : 'Send Connection'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConnectModal;
